import { uiText } from '../i18n/uiText';
import React, { useEffect, useState, useMemo } from 'react';
import {
  Upload, X, CheckCircle, Save, Send, FileCheck, Scale, Wallet, Award,
  TrendingUp, AlertCircle, CheckCircle2, Sparkles, Pencil,
} from 'lucide-react';
import { selfCheckApi, getApiErrorMessage } from '../api';
import { useConfirm } from '../store/ConfirmContext';
import { LoadingState } from '../components/common/UI';
import PageIntro from '../components/ui/PageIntro';
import { useAuth } from '../store/AuthContext';
import useDraftBuffer from '../hooks/useDraftBuffer';
import FormDraftNotice from '../components/common/FormDraftNotice';

const BLOCK_META = {
  legal: {
    label: uiText("Юридическая состоятельность"),
    desc: uiText("ЕГРЮЛ, лицензии, СРО, РНП — базовая правомочность контрагента"),
    icon: Scale,
    bg: '#ffffff',
    accent: '#27303f',
    border: '#e6e8ec',
  },
  financial: {
    label: uiText("Финансовая устойчивость"),
    desc: uiText("Бух. отчётность, налоги, выручка"),
    icon: Wallet,
    bg: '#ffffff',
    accent: '#27303f',
    border: '#e6e8ec',
  },
  reputation: {
    label: uiText("Репутация и опыт"),
    desc: uiText("Референсы, судебные акты"),
    icon: Award,
    bg: '#ffffff',
    accent: '#27303f',
    border: '#e6e8ec',
  },
};

const COLOR_META = {
  green:  { label: uiText("Зелёный (надёжный)"),    bg: '#dcfce7', color: '#15803d', icon: CheckCircle2 },
  yellow: { label: uiText("Жёлтый (внимание)"),      bg: '#fef3c7', color: '#a16207', icon: AlertCircle },
  red:    { label: uiText("Красный (критический)"),  bg: '#fee2e2', color: '#b91c1c', icon: AlertCircle },
};

export default function SelfCheckPage() {
  const confirm = useConfirm();
  const [questions, setQuestions] = useState([]);
  const { user } = useAuth();
  const [serverCheck, setCheck] = useState(null);
  const buffer = useDraftBuffer(user?.id, serverCheck?.id ?? 'loading', 'self-check');
  const check = serverCheck ? { ...serverCheck, answers: buffer.values.answers || serverCheck.answers } : null;
  useEffect(() => { if (serverCheck) buffer.hydrate({ answers: serverCheck.answers || {} }); }, [serverCheck, buffer]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    setError('');
    try {
      const [defRes, myRes] = await Promise.all([
        selfCheckApi.definition(),
        selfCheckApi.my(),
      ]);
      setQuestions(defRes.data.questions || []);
      setCheck(myRes.data);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const isReadonly = check && check.status !== 'draft';

  const grouped = useMemo(() => {
    const out = { legal: [], financial: [], reputation: [] };
    questions.forEach(q => { if (out[q.block]) out[q.block].push(q); });
    return out;
  }, [questions]);

  // Считаем прогресс заполнения (для прогресс-бара)
  const progress = useMemo(() => {
    const total = questions.length || 1;
    const answers = check?.answers || {};
    const answered = questions.filter(q => {
      const a = answers[q.id];
      const val = a && typeof a === 'object' ? a.value : a;
      return val === true || val === false;
    }).length;
    const required = questions.filter(q => q.required).length || 1;
    const requiredDone = questions.filter(q => {
      if (!q.required) return false;
      const a = answers[q.id];
      const val = a && typeof a === 'object' ? a.value : a;
      return val === true || val === false;
    }).length;
    return {
      answered, total, pct: Math.round((answered / total) * 100),
      required, requiredDone,
      requiredPct: Math.round((requiredDone / required) * 100),
    };
  }, [questions, check]);

  const setAnswer = (qid, value) => {
    if (busy || isReadonly || buffer.recovery) return;
    const answers = buffer.values.answers || check?.answers || {};
    buffer.edit('answers', { ...answers, [qid]: { value, comment: answers[qid]?.comment || '' } });
  };

  const saveDraft = async () => {
    if (busy || isReadonly || buffer.recovery) return;
    const snapshot = buffer.snapshot();
    setError(''); setSuccess(''); setBusy(true);
    try {
      const res = await selfCheckApi.saveAnswers(check?.answers || {});
      buffer.acknowledge(snapshot, { committed: true });
      setCheck(res.data);
      setSuccess(uiText("Черновик сохранён"));
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(getApiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (busy || isReadonly || buffer.recovery) return;
    if (!await confirm(
      uiText("Отправить опросник на проверку?\n\n") +
      uiText("После отправки изменения недоступны до того как аудитор откроет его повторно. Документы можно не прикладывать, но без них аудитору может потребоваться дополнительное подтверждение.")
    )) return;
    if (!buffer.active) return;
    const snapshot = buffer.snapshot();
    setError(''); setSuccess(''); setBusy(true);
    try {
      await selfCheckApi.saveAnswers(check?.answers || {});
      buffer.acknowledge(snapshot, { committed: true });
      const res = await selfCheckApi.submit();
      setCheck(res.data);
      setSuccess(uiText("Опросник отправлен на проверку"));
    } catch (e) { setError(getApiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const uploadDoc = async (questionId, docType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = async () => {
      if (!input.files?.[0]) return;
      const fd = new FormData();
      fd.append('file', input.files[0]);
      fd.append('doc_type', docType);
      fd.append('checklist_item', String(questionId));
      try {
        const res = await selfCheckApi.uploadDocument(fd);
        setCheck(res.data);
      } catch (e) { setError(getApiErrorMessage(e)); }
    };
    input.click();
  };

  const deleteDoc = async (id) => {
    if (!await confirm(uiText("Удалить документ?"), { title: uiText("Удаление документа"), confirmLabel: uiText("Удалить") })) return;
    try {
      const res = await selfCheckApi.deleteDocument(id);
      setCheck(res.data);
    } catch (e) { setError(getApiErrorMessage(e)); }
  };

  const docsByQuestion = useMemo(() => {
    const out = {};
    (check?.documents || []).forEach(d => {
      if (d.checklist_item) {
        if (!out[d.checklist_item]) out[d.checklist_item] = [];
        out[d.checklist_item].push(d);
      }
    });
    return out;
  }, [check]);

  if (loading) {
    return <LoadingState message={uiText("Загружаем анкету…")} />;
  }

  const showRating = check?.status === 'reviewed' && check?.rating_score != null && check?.rating_color;
  const ratingCfg = showRating ? COLOR_META[check.rating_color] : null;

  return (
    <div className="page-content self-check-page">
      {!isReadonly && <FormDraftNotice buffer={buffer} label={uiText("Анкета организации")} disabled={busy} />}
      <PageIntro
        icon={Sparkles}
        title={uiText("Анкета организации")}
        description={uiText("Заполните анкету в трёх блоках и приложите подтверждающие документы. Рейтинг станет доступен после проверки аудитором и не показывается как предварительный результат.")}
      />

      {!isReadonly && (
        <div className="self-check-guidance" role="note">{uiText("Ответьте на все обязательные вопросы. Если требование неприменимо к вашей организации, выберите «Да». Подтверждающие документы рекомендуются, но не блокируют отправку; они помогут аудитору быстрее проверить ответы.")}</div>
      )}

      {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>{error}</div>}
      {success && (
        <div className="alert self-check-success" style={{
          background: '#dcfce7', border: '1px solid #86efac', color: '#15803d',
          marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <CheckCircle2 size={16}/> {success}
        </div>
      )}

      {/* Top status card: рейтинг + статус + прогресс */}
      <div className="card self-check-status-card" style={{ marginBottom: 20, overflow: 'hidden' }}>
        <div className="self-check-status-panel" style={{
          padding: 20,
          background: ratingCfg
            ? `linear-gradient(135deg, ${ratingCfg.bg} 0%, white 70%)`
            : 'linear-gradient(135deg, #f1f5f9 0%, white 70%)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            {ratingCfg ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div className="self-check-status-icon" style={{
                  width: 56, height: 56, borderRadius: 14,
                  background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <ratingCfg.icon size={26} style={{ color: ratingCfg.color }}/>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{uiText("Ваш рейтинг")}</div>
                  <div style={{
                    fontSize: 18, fontWeight: 700, color: ratingCfg.color,
                  }}>{ratingCfg.label}</div>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div className="self-check-status-icon" style={{
                  width: 56, height: 56, borderRadius: 14,
                  background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <FileCheck size={26} style={{ color: '#94a3b8' }}/>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{uiText("Рейтинг")}</div>
                  <div style={{ fontWeight: 600, color: '#475569' }}>{uiText("Будет доступен после проверки")}</div>
                </div>
              </div>
            )}

            {showRating && (
              <div style={{ borderLeft: '1px solid rgba(0,0,0,0.08)', paddingLeft: 20 }}>
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{uiText("Балл")}</div>
                <div style={{
                  fontSize: 32, fontWeight: 800, lineHeight: 1,
                  color: ratingCfg?.color || '#475569',
                }}>{check.rating_score}<span style={{ fontSize: 14, color: '#94a3b8', fontWeight: 500 }}>/100</span></div>
              </div>
            )}

            <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{uiText("Статус")}</div>
              <div className="workspace-inline-meta" style={{ fontWeight: 700, justifyContent: 'flex-end' }}>
                {check?.status === 'draft' && <><Pencil size={14} />{uiText(" Черновик")}</>}
                {check?.status === 'submitted' && <><Send size={14} />{uiText(" Отправлен на проверку")}</>}
                {check?.status === 'reviewed' && <><CheckCircle2 size={14} />{uiText(" Проверен")}</>}
              </div>
            </div>
          </div>

          {/* Прогресс */}
          {!isReadonly && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 6 }}>
                <span>{uiText("Заполнено: ")}<b style={{ color: '#1e293b' }}>{progress.answered}</b>{uiText(" из ")}{progress.total}{uiText(" вопросов")}</span>
                <span>{uiText("Обязательные: ")}<b style={{ color: progress.requiredPct === 100 ? '#15803d' : '#a16207' }}>
                  {progress.requiredDone}</b> / {progress.required}</span>
              </div>
              <div className="self-check-progress-track" style={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  width: `${progress.pct}%`, height: '100%',
                  background: progress.pct === 100 ? '#24795a' : '#b52b49',
                  transition: 'width 0.4s ease',
                }}/>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Блоки вопросов */}
      {Object.entries(grouped).map(([blockKey, qs]) => {
        const meta = BLOCK_META[blockKey];
        if (!meta) return null;
        const Icon = meta.icon;
        const blockAnswered = qs.filter(q => {
          const a = check?.answers?.[q.id];
          const v = a && typeof a === 'object' ? a.value : a;
          return v === true || v === false;
        }).length;

        return (
          <div key={blockKey} className={`card self-check-block self-check-block-${blockKey}`} style={{ marginBottom: 16, overflow: 'hidden' }}>
            <div className="self-check-block-header" style={{
              padding: '16px 20px', background: meta.bg, borderBottom: `1px solid ${meta.border}`,
              display: 'flex', alignItems: 'center', gap: 14,
            }}>
              <div className="self-check-block-icon" style={{
                width: 40, height: 40, borderRadius: 10, background: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              }}>
                <Icon size={20} style={{ color: meta.accent }}/>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: meta.accent }}>{meta.label}</div>
                <div style={{ fontSize: 12, color: meta.accent, opacity: 0.7 }}>{meta.desc}</div>
              </div>
              <div className="self-check-block-count" style={{
                padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                background: 'white', color: meta.accent,
              }}>{blockAnswered}/{qs.length}</div>
            </div>

            <div style={{ padding: 0 }}>
              {qs.map((q, idx) => {
                const a = check?.answers?.[q.id] || {};
                const docs = docsByQuestion[q.order] || [];
                const isAnswered = a.value === true || a.value === false;

                return (
                  <div key={q.id} className={`self-check-question${isAnswered ? ' is-answered' : ''}`} style={{
                    padding: '14px 20px',
                    borderBottom: idx < qs.length - 1 ? '1px solid #f1f5f9' : 'none',
                    display: 'flex', gap: 14, alignItems: 'flex-start',
                    background: isAnswered ? '#fafafa' : 'white',
                    transition: 'background 0.15s',
                  }}>
                    <div className={`self-check-question-number${isAnswered ? (a.value ? ' is-yes' : ' is-no') : ''}`} style={{
                      width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                      fontWeight: 700, fontSize: 12,
                      background: isAnswered ? (a.value ? '#dcfce7' : '#fee2e2') : '#f1f5f9',
                      color: isAnswered ? (a.value ? '#15803d' : '#b91c1c') : '#64748b',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>{q.order}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>
                        {q.title}
                        {q.required && <span style={{ color: '#b91c1c', marginLeft: 4 }}>*</span>}
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>{q.description}</div>

                      <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <button type="button" disabled={isReadonly}
                          className={`self-check-choice is-yes${a.value === true ? ' is-selected' : ''}`}
                          onClick={() => setAnswer(q.id, true)}
                          style={{
                            padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                            cursor: isReadonly ? 'default' : 'pointer',
                            border: a.value === true ? '1px solid #15803d' : '1px solid #e2e8f0',
                            background: a.value === true ? '#dcfce7' : 'white',
                            color: a.value === true ? '#15803d' : '#475569',
                            transition: 'all 0.15s',
                          }}>{uiText("✓ Да")}</button>
                        <button type="button" disabled={isReadonly}
                          className={`self-check-choice is-no${a.value === false ? ' is-selected' : ''}`}
                          onClick={() => setAnswer(q.id, false)}
                          style={{
                            padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                            cursor: isReadonly ? 'default' : 'pointer',
                            border: a.value === false ? '1px solid #b91c1c' : '1px solid #e2e8f0',
                            background: a.value === false ? '#fee2e2' : 'white',
                            color: a.value === false ? '#b91c1c' : '#475569',
                            transition: 'all 0.15s',
                          }}>{uiText("✗ Нет")}</button>
                        {!isReadonly && (
                          <button type="button" className="self-check-upload" onClick={() => uploadDoc(q.order, q.doc_type)}
                            style={{
                              padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500,
                              cursor: 'pointer', border: '1px dashed #cbd5e1', background: 'white',
                              color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 4,
                            }}
                          ><Upload size={12}/>{uiText(" Документ")}</button>
                        )}
                      </div>

                      {docs.length > 0 && (
                        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {docs.map(d => (
                            <div key={d.id} className="self-check-document" style={{
                              display: 'flex', gap: 8, alignItems: 'center', fontSize: 12,
                              padding: '6px 10px', background: 'white',
                              border: '1px solid #e2e8f0', borderRadius: 6,
                            }}>
                              <CheckCircle size={13} style={{ color: '#15803d', flexShrink: 0 }}/>
                              <span style={{ flex: 1 }}>{d.original_name}</span>
                              <span style={{ color: '#94a3b8', fontSize: 11 }}>{Math.round((d.file_size || 0) / 1024)} KB</span>
                              {!isReadonly && (
                                <button type="button" onClick={() => deleteDoc(d.id)} style={{
                                  border: 'none', background: 'transparent', cursor: 'pointer',
                                  color: '#94a3b8', display: 'flex', alignItems: 'center', padding: 2,
                                }}><X size={13}/></button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Bottom action bar */}
      {!isReadonly && (
        <div className="self-check-actionbar" style={{
          position: 'sticky', bottom: 0,
          marginTop: 20, padding: 14, borderRadius: 12,
          background: 'white', border: '1px solid #e2e8f0',
          boxShadow: '0 -4px 12px -2px rgba(0,0,0,0.05)',
          display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap',
        }}>
          <div style={{ marginRight: 'auto', fontSize: 12, color: '#64748b' }}>
            {progress.requiredPct < 100
              ? <span className="workspace-inline-meta"><AlertCircle size={14} />{uiText(" Заполнено ")}<b>{progress.requiredDone}</b>/{progress.required}{uiText(" обязательных пунктов")}</span>
              : <>{uiText("✓ Все обязательные пункты заполнены")}</>}
          </div>
          <button type="button" className="btn btn-outline" onClick={saveDraft} disabled={busy}>
            <Save size={14}/>{uiText(" Сохранить черновик")}</button>
          <button type="button" className="btn btn-primary" onClick={submit}
            disabled={busy || progress.requiredPct < 100}>
            <Send size={14}/>{uiText(" Отправить на проверку")}</button>
        </div>
      )}

      {isReadonly && check?.status === 'reviewed' && (
        <div className="card self-check-reviewed" style={{
          marginTop: 14, background: 'linear-gradient(90deg, #ecfdf5 0%, #fff 100%)',
          border: '1px solid #86efac',
        }}>
          <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <CheckCircle2 size={20} style={{ color: '#15803d' }}/>
            <div>
              <div style={{ fontWeight: 600 }}>{uiText("Опросник проверен и рейтинг присвоен")}</div>
              {check.notes && <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>{uiText("Комментарий: ")}{check.notes}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
